import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { exportAPI, usersAPI, corporatesAPI } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  FileSpreadsheet,
  Download,
  Filter,
  Calendar,
  FileText,
  DollarSign,
  Bell,
  Users,
  Loader2
} from "lucide-react";
import { cn } from "../lib/utils";

const sheetIcons = {
  contratos: FileText,
  cotizaciones: FileText,
  cxc: DollarSign,
  cxp: DollarSign,
  comisiones: DollarSign,
  ledger: DollarSign,
  alertas: Bell
};

export default function Exportar() {
  const { user, isAdmin, isVendedor, isLogistica } = useAuth();
  const [availableSheets, setAvailableSheets] = useState([]);
  const [selectedSheets, setSelectedSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [corporates, setCorporates] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    vendor_id: "",
    status: "",
    traffic_light: "",
    corporate_id: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sheetsRes, vendorsRes, corporatesRes] = await Promise.all([
        exportAPI.getAvailableSheets(),
        isAdmin ? usersAPI.getAll() : Promise.resolve({ data: [] }),
        corporatesAPI.getAll()
      ]);
      
      setAvailableSheets(sheetsRes.data);
      setSelectedSheets(sheetsRes.data.map(s => s.id));
      
      if (isAdmin) {
        setVendors(vendorsRes.data.filter(u => u.role === "vendedor"));
      }
      setCorporates(corporatesRes.data);
    } catch (error) {
      toast.error("Error al cargar opciones de exportación");
    } finally {
      setLoading(false);
    }
  };

  const handleSheetToggle = (sheetId) => {
    setSelectedSheets(prev => 
      prev.includes(sheetId) 
        ? prev.filter(s => s !== sheetId)
        : [...prev, sheetId]
    );
  };

  const handleSelectAll = () => {
    if (selectedSheets.length === availableSheets.length) {
      setSelectedSheets([]);
    } else {
      setSelectedSheets(availableSheets.map(s => s.id));
    }
  };

  const handleExport = async () => {
    if (selectedSheets.length === 0) {
      toast.error("Seleccione al menos una hoja para exportar");
      return;
    }

    setExporting(true);
    try {
      const response = await exportAPI.exportExcel({
        sheets: selectedSheets,
        ...filters
      });

      // Download the file
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tenatour_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Archivo exportado exitosamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al exportar");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 lg:p-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/3"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="export-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-3">
              <FileSpreadsheet className="w-7 h-7 text-green-600" />
              Exportar a Excel
            </h1>
            <p className="text-slate-500 mt-1">
              Descarga los datos del sistema en formato Excel (.xlsx)
            </p>
          </div>
          <Button 
            onClick={handleExport} 
            disabled={exporting || selectedSheets.length === 0}
            className="gap-2"
            data-testid="export-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {exporting ? "Exportando..." : "Exportar Excel"}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sheets Selection */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-heading">Hojas a Exportar</CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleSelectAll}
                    data-testid="select-all-btn"
                  >
                    {selectedSheets.length === availableSheets.length ? "Deseleccionar Todo" : "Seleccionar Todo"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {availableSheets.map((sheet) => {
                    const Icon = sheetIcons[sheet.id] || FileText;
                    const isSelected = selectedSheets.includes(sheet.id);
                    
                    return (
                      <div
                        key={sheet.id}
                        onClick={() => handleSheetToggle(sheet.id)}
                        className={cn(
                          "p-4 rounded-lg border-2 cursor-pointer transition-all",
                          isSelected 
                            ? "border-green-500 bg-green-50" 
                            : "border-slate-200 hover:border-slate-300"
                        )}
                        data-testid={`sheet-${sheet.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleSheetToggle(sheet.id)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Icon className={cn(
                                "w-4 h-4",
                                isSelected ? "text-green-600" : "text-slate-400"
                              )} />
                              <span className={cn(
                                "font-medium",
                                isSelected ? "text-green-900" : "text-slate-700"
                              )}>
                                {sheet.name}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              {sheet.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {availableSheets.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No tiene permisos para exportar datos</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filtros
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Fecha Inicio</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                    data-testid="start-date-filter"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="end_date">Fecha Fin</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                    data-testid="end-date-filter"
                  />
                </div>

                {isAdmin && vendors.length > 0 && (
                  <div className="space-y-2">
                    <Label>Vendedor</Label>
                    <Select
                      value={filters.vendor_id || "all"}
                      onValueChange={(v) => setFilters(prev => ({ ...prev, vendor_id: v === "all" ? "" : v }))}
                    >
                      <SelectTrigger data-testid="vendor-filter">
                        <SelectValue placeholder="Todos los vendedores" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los vendedores</SelectItem>
                        {vendors.map(v => (
                          <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Semáforo</Label>
                  <Select
                    value={filters.traffic_light || "all"}
                    onValueChange={(v) => setFilters(prev => ({ ...prev, traffic_light: v === "all" ? "" : v }))}
                  >
                    <SelectTrigger data-testid="traffic-light-filter">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="rojo">🔴 Rojo</SelectItem>
                      <SelectItem value="amarillo">🟡 Amarillo</SelectItem>
                      <SelectItem value="verde">🟢 Verde</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Select
                    value={filters.status || "all"}
                    onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === "all" ? "" : v }))}
                  >
                    <SelectTrigger data-testid="status-filter">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="contrato">Contrato</SelectItem>
                      <SelectItem value="en_curso">En Curso</SelectItem>
                      <SelectItem value="completado">Completado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {corporates.length > 0 && (
                  <div className="space-y-2">
                    <Label>Corporativo</Label>
                    <Select
                      value={filters.corporate_id || "all"}
                      onValueChange={(v) => setFilters(prev => ({ ...prev, corporate_id: v === "all" ? "" : v }))}
                    >
                      <SelectTrigger data-testid="corporate-filter">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {corporates.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setFilters({
                    start_date: "",
                    end_date: "",
                    vendor_id: "",
                    status: "",
                    traffic_light: "",
                    corporate_id: ""
                  })}
                >
                  Limpiar Filtros
                </Button>
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="mt-4 bg-slate-50">
              <CardContent className="p-4">
                <h4 className="font-medium text-sm text-slate-700 mb-2">Información</h4>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>• El archivo se descargará en formato .xlsx</li>
                  <li>• Cada módulo se exporta en una hoja separada</li>
                  <li>• Los filtros aplican a todas las hojas</li>
                  <li>• La exportación queda registrada en auditoría</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
